import { Label } from '../../components/ui/label';
import { Icons } from '../../icons';
import { cn, testId } from '../../utils/ui.util';
import { Typography, Input, InputType, Iconography } from '../primitives';
import type { InputProps } from '../primitives/Input';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import clsx from 'clsx';
import * as React from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  GlobalError,
  useFormContext,
} from 'react-hook-form';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formLabelId: `${id}-form-item-label`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        ref={ref}
        className={clsx('flex flex-col gap-1', className)}
        {...props}
      />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  Omit<
    React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>,
    'id' | 'htmlFor'
  > & { 'data-testid': string }
>(({ className, ...props }, ref) => {
  const { formItemId, formLabelId } = useFormField();

  return (
    <Label
      ref={ref}
      id={formLabelId}
      className={className}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formLabelId, formMessageId } =
    useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-labelledby={formLabelId}
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  Omit<React.HTMLAttributes<HTMLParagraphElement>, 'id'>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn('text-sm text-slate-500 dark:text-slate-400', className)}
      {...props}
    />
  );
});
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef<HTMLDivElement, { 'data-testid': string }>(
  ({ 'data-testid': dataTestId }, ref) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message) : null;

    if (!body) {
      return null;
    }

    return (
      <div
        {...testId(dataTestId)}
        data-error={error?.type}
        ref={ref}
        id={formMessageId}
        className={cn('text-xs font-medium text-red-dark flex gap-1')}
        role="alert"
      >
        <Iconography variant="error" className="text-base mt-[2px]" />
        <span>{body}</span>
      </div>
    );
  },
);
FormMessage.displayName = 'FormMessage';

const FormError = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement> & {
    error?: GlobalError;
    'data-testid': string;
  }
>(
  (
    { error, className, children, 'data-testid': dataTestId, ...props },
    ref,
  ) => {
    if (!error) {
      return null;
    }

    return (
      <p
        {...testId(dataTestId)}
        ref={ref}
        className={cn(
          'text-sm font-medium text-red-500 dark:text-red-900',
          className,
        )}
        {...props}
      >
        {error.message}
      </p>
    );
  },
);
FormError.displayName = 'FormError';

const TextField = React.forwardRef<
  HTMLDivElement,
  Omit<InputProps, 'type'> & { label: React.ReactNode; required?: boolean }
>(
  (
    {
      label,
      required,
      invalid,
      className,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    return (
      <FormItem className={className} ref={ref}>
        <FormLabel {...testId(dataTestId, 'label')}>
          <Typography variant="form-label">
            {label}
            {required && (
              <>
                {' '}
                <Icons.Indicator />
              </>
            )}
          </Typography>
        </FormLabel>
        <FormControl>
          <Input
            {...testId(dataTestId)}
            {...props}
            type={InputType.TEXT}
            placeholder=""
            invalid={invalid}
          />
        </FormControl>
        <FormMessage {...testId(dataTestId, 'message')} />
      </FormItem>
    );
  },
);
TextField.displayName = 'TextField';

export {
  Form,
  FormControl,
  FormDescription,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
  TextField,
};
